/**
 * EndDialogNode - Node component for 'end-dialog' type fields
 * 
 * Terminal node that ends the dialog.
 * Has NO source handles - this is where the conversation ends.
 */

import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { NodeProps } from '@xyflow/react';
import IconProvider from '@/components/ui/IconProvider';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { DialogField } from '@/lib/stores/dialogStore';
import { BaseDialogNode } from './BaseDialogNode';
import { DialogFieldNodeData, getFieldTypeIcon, getFieldTypeColor } from './dialogNodeUtils';
import { useDisplayLanguageOptional, resolveFieldDisplayText, resolveFieldEditableText, saveFieldTranslation } from '@/contexts/DisplayLanguageContext';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

export const EndDialogNode = memo(({ id, data, selected }: NodeProps<any>) => {
  const nodeData = data as DialogFieldNodeData;
  const field = nodeData.field;
  const { selectedGame } = useSimplifiedUserData();
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const effectiveGameId = selectedGame?.id;
  const [isEditing, setIsEditing] = useState(false);
  const [editedType, setEditedType] = useState(field.type);
  const [editedText, setEditedText] = useState(field.text || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevSelectedRef = React.useRef(selected);

  // Attach native event listeners to prevent dragging
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !isEditing) return;

    const handlePointerDown = (e: PointerEvent) => {
      e.stopImmediatePropagation();
    };

    const handleMouseDown = (e: MouseEvent) => {
      e.stopImmediatePropagation();
    };

    // Use capture phase to run before React Flow's listeners
    textarea.addEventListener('pointerdown', handlePointerDown, { capture: true });
    textarea.addEventListener('mousedown', handleMouseDown, { capture: true });
    
    return () => {
      textarea.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      textarea.removeEventListener('mousedown', handleMouseDown, { capture: true });
    };
  }, [isEditing]);

  // Sync local state with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditedType(field.type);
      setEditedText(field.text || '');
    }
  }, [field, isEditing]);

  // Are we editing a translation (non-source language + TMS-linked field)?
  const isTranslationMode = displayLanguage !== null && !!field.string_id && !!field.translatable;

  const handleSaveChanges = useCallback(() => {
    if (isTranslationMode && field.string_id && effectiveGameId && displayLanguage) {
      saveFieldTranslation(field.string_id, displayLanguage, editedText, effectiveGameId);
      if (editedType !== field.type && nodeData.onFieldUpdate) {
        nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, { type: editedType });
      }
    } else if (nodeData.onFieldUpdate) {
      nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, {
        type: editedType,
        text: editedText,
      });
    }
    setIsEditing(false);
  }, [editedType, editedText, nodeData, isTranslationMode, field.string_id, field.type, displayLanguage, effectiveGameId]);

  // Auto-save when node loses selection (clicking outside)
  useEffect(() => {
    // Only auto-save if we're transitioning from selected to unselected while editing
    if (prevSelectedRef.current && !selected && isEditing) {
      handleSaveChanges();
    }
    // Update ref for next render
    prevSelectedRef.current = selected;
  }, [selected, isEditing, handleSaveChanges]);

  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      handleSaveChanges();
    } else {
      setIsEditing(true);
      setEditedType(field.type);
      setEditedText(resolveFieldEditableText(field, displayLanguage, effectiveGameId) || '');
    }
  }, [isEditing, handleSaveChanges, field, displayLanguage, effectiveGameId]);

  const colors = getFieldTypeColor(isEditing ? editedType : field.type);

  // Header content for edit mode (with type selector)
  const headerContent = isEditing ? (
    <>
      <IconProvider iconstring={getFieldTypeIcon(editedType)} size={14} className={colors.text} />
      <Select value={editedType} onValueChange={(value: any) => setEditedType(value)}>
        <SelectTrigger className="h-6 text-xs flex-1 bg-white noDrag">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="dialog">Dialog</SelectItem>
          <SelectItem value="question">Question</SelectItem>
          <SelectItem value="context">Context</SelectItem>
          <SelectItem value="end-dialog">End Dialog</SelectItem>
        </SelectContent>
      </Select>
    </>
  ) : undefined;

  return (
    <BaseDialogNode
      id={id}
      data={nodeData}
      selected={selected}
      isEditing={isEditing}
      onEditToggle={handleEditToggle}
      headerContent={headerContent}
    >
      {/* NO source handle - this is a terminal node */}

      {/* Text content */}
      {isEditing ? (
        <Textarea
          ref={textareaRef}
          value={editedText}
          onChange={(e) => setEditedText(e.target.value)}
          className="text-sm mb-2 min-h-[60px] noDrag"
          placeholder="Optional end dialog message..."
        />
      ) : (
        field.text && (
          <div className="text-sm mb-2">
            {resolveFieldDisplayText(field, displayLanguage, effectiveGameId)}
          </div>
        )
      )}

      {/* End message */}
      <div className="text-center py-2">
        {!isEditing && !field.text && (
          <div className="text-sm text-muted-foreground mb-2">
            Dialog ends here
          </div>
        )}
        
        {/* End badge */}
        <div className="flex items-center justify-center gap-2 text-xs">
          <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded">
            <IconProvider iconstring="fa-circle-stop" size={10} />
            <span>End</span>
          </div>
        </div>
      </div>
    </BaseDialogNode>
  );
});

EndDialogNode.displayName = 'EndDialogNode';

export default EndDialogNode;
